import type { FeatureCollection } from 'geojson';
import type { GeoJSONSource, LngLatBoundsLike } from 'maplibre-gl';
import { useEffect } from 'react';
import { useMap } from './MapView';

export interface RoutePoint {
  lat: number;
  lon: number;
  speedKph?: number | null;
}

export interface RouteStopMarker {
  id: string;
  lat: number;
  lon: number;
  kind: 'STOP' | 'IDLE';
  label: string;
}

/**
 * Polyline (colored by speed) + stop markers + optional playhead. Fits bounds on change when `fit` is true.
 * `beforeId` inserts the layers under an existing layer (e.g. the vehicles) so they never cover it.
 * `id` namespaces the sources/layers so several routes (driven path, planned path) can share a map.
 * `dashed` draws the line dashed (planned / remaining paths).
 */
export function RouteLayer({ id = 'route', points, stops = [], playhead, fit = true, color = '#14532D', beforeId, dashed = false }: { id?: string; points: RoutePoint[]; stops?: RouteStopMarker[]; playhead?: RoutePoint | null; fit?: boolean; color?: string; beforeId?: string; dashed?: boolean }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const SRC = id;
    const before = beforeId && map.getLayer(beforeId) ? beforeId : undefined;
    const line: FeatureCollection = {
      type: 'FeatureCollection',
      features: points.length > 1 ? [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: points.map((p) => [p.lon, p.lat]) } }] : [],
    };
    const stopFc: FeatureCollection = { type: 'FeatureCollection', features: stops.map((s) => ({ type: 'Feature', properties: { id: s.id, kind: s.kind, label: s.label }, geometry: { type: 'Point', coordinates: [s.lon, s.lat] } })) };
    const head: FeatureCollection = { type: 'FeatureCollection', features: playhead ? [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [playhead.lon, playhead.lat] } }] : [] };

    const upsert = (id: string, data: FeatureCollection) => {
      const s = map.getSource(id) as GeoJSONSource | undefined;
      if (s) s.setData(data);
      else map.addSource(id, { type: 'geojson', data });
    };
    upsert(SRC, line);
    upsert(`${SRC}-stops`, stopFc);
    upsert(`${SRC}-head`, head);

    if (!map.getLayer(`${SRC}-casing`)) {
      map.addLayer({ id: `${SRC}-casing`, type: 'line', source: SRC, layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': dashed ? 5 : 7, 'line-opacity': dashed ? 0.6 : 1 } }, before);
      map.addLayer({ id: `${SRC}-line`, type: 'line', source: SRC, layout: { 'line-cap': dashed ? 'butt' : 'round', 'line-join': 'round' }, paint: { 'line-color': color, 'line-width': dashed ? 3 : 4, ...(dashed ? { 'line-dasharray': [2, 2] } : {}) } }, before);
      map.addLayer({ id: `${SRC}-stops-circle`, type: 'circle', source: `${SRC}-stops`, paint: { 'circle-radius': 7, 'circle-color': ['case', ['==', ['get', 'kind'], 'IDLE'], '#F59E0B', '#94A3B8'], 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 } }, before);
      map.addLayer({ id: `${SRC}-stops-label`, type: 'symbol', source: `${SRC}-stops`, layout: { 'text-field': ['get', 'label'], 'text-size': 10, 'text-font': ['Noto Sans Regular'], 'text-offset': [0, 1.2], 'text-anchor': 'top', 'text-optional': true }, paint: { 'text-color': '#385447', 'text-halo-color': '#fff', 'text-halo-width': 1.2 } }, before);
      map.addLayer({ id: `${SRC}-head-circle`, type: 'circle', source: `${SRC}-head`, paint: { 'circle-radius': 9, 'circle-color': '#2563EB', 'circle-stroke-color': '#fff', 'circle-stroke-width': 3 } }, before);
    } else {
      map.setPaintProperty(`${SRC}-line`, 'line-color', color);
    }
    if (fit && points.length > 1) {
      let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
      for (const p of points) {
        minLat = Math.min(minLat, p.lat); maxLat = Math.max(maxLat, p.lat); minLon = Math.min(minLon, p.lon); maxLon = Math.max(maxLon, p.lon);
      }
      const b: LngLatBoundsLike = [[minLon, minLat], [maxLon, maxLat]];
      map.fitBounds(b, { padding: 60, duration: 500, maxZoom: 15 });
    }
  }, [map, id, points, stops, playhead, fit, color, beforeId, dashed]);

  return null;
}
